import { ReactElement, useEffect } from 'react'
import styled from 'styled-components'

import Wrapper from 'containers/Wrapper'

import Footer from 'components/Footer'
import Image from 'components/common/Image'
import { Button, Typography } from 'components/common'

import useStore from 'hooks/useStore'

import { redirect } from 'helpers/window'

import { IMetaData } from 'Errors/StandardError'

interface NewErrorPageProps {
    title: string
    description: string
    image: string
    redirectURL?: string
    metaData?: IMetaData
    cta?: {
        id: string
        schema_version: string
        button_text: string
    }
}

const MS_PER_SECOND = 1000

const NewErrorPage = ({
    title,
    description,
    image,
    redirectURL,
    metaData,
    cta,
}: NewErrorPageProps): ReactElement => {
    const [store] = useStore()
    const redirectURLFromMetaData = (): string | undefined => {
        if (!store.paymentDetails?.redirect_url || !metaData) return
        const redirectURL = new URL(store.paymentDetails?.redirect_url)
        redirectURL.searchParams.append('errorCode', metaData?.errorCode)
        redirectURL.searchParams.append('errorDesc', metaData.errorDescription)
        return redirectURL.toString()
    }
    const redirectURLForSamsung = (): string | undefined => {
        if (!redirectURL || !metaData || !store.paymentReviewData) return
        const errorRedirectURL = `${redirectURL}?reference=${store.paymentReviewData.payment_id}&status=FAILED&error=${metaData.errorDescription}`
        return errorRedirectURL
    }
    const instrumentType: InstrumentTypes =
        store.selectedPaymentInstructions?.[0]?.instrumentType ??
        store.paymentDetails?.payment_instructions?.[0].instrument_type ??
        'GOPAY_WALLET'

    const onErrorButtonClick = () => {
        const url = redirectURLForSamsung()

        if (url) {
            redirect(url)
        }
    }

    useEffect(() => {
        const errorRedirectURL =
            redirectURLFromMetaData() ?? redirectURLForSamsung() ?? redirectURL

        if (errorRedirectURL) {
            setTimeout(() => {
                redirect(errorRedirectURL)
            }, process.env.REACT_APP_REDIRECT_TO_MERCHANT_ON_ERROR_INTERVAL_IN_SECONDS * MS_PER_SECOND)
        }
    }, [])

    return (
        <div data-testid="error-page">
            <Wrapper instrumentType={instrumentType} data-testid="error-page">
                <Image
                    data-testid="error-image"
                    alt="error"
                    src={image}
                    width="343px"
                    height="100%"
                    style={{ margin: '0 auto 1rem auto', maxWidth: '100%' }}
                />
                <Title data-testid="error-title">{title}</Title>
                <Description data-testid="error-description">
                    {description}
                </Description>

                <FooterContainer>
                    {cta && (
                        <Button
                            id={cta.id}
                            data-testid="error-cta-button"
                            block
                            centering
                            className="mb-5"
                            onClick={onErrorButtonClick}
                        >
                            <Typography.Title
                                variant="small"
                                weight="font-bold"
                            >
                                {cta.button_text}
                            </Typography.Title>
                        </Button>
                    )}
                    <Footer />
                </FooterContainer>
            </Wrapper>
        </div>
    )
}

const Title = styled.h4`
    font-size: 1.3125rem;
    font-weight: 600;
    line-height: 1.875rem;
    letter-spacing: 0.3%;
    text-align: center;
    letter-spacing: 0.003rem;
    color: ${({ theme }) => theme.colors?.text?.primary};
`

const Description = styled.div`
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.5rem;
    text-align: center;
    letter-spacing: 0.015rem;
    color: ${({ theme }) => theme.colors?.text?.description};
    margin-top: 0.25rem;
`

const FooterContainer = styled.div`
    margin-top: auto;
    margin-bottom: 1.75rem;
`

export default NewErrorPage
